import { Dispatch, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "@unified-checkout/design-system/icons/index";
import type { TCountry, IField } from "@app/dashboard/types/outerLayout/common";
import { useLangStore } from "@app/dashboard/store/lang";
import { OUTER_ROUTES } from "@app/dashboard/routes/outer-routes";
import { getCountryFromDomain } from "@app/dashboard/utils/getCountryFromDomain";
import {
  passwordsComparisonLiteral,
  validateField as fieldValidator
} from '@app/dashboard/utils/formValidations';
import FormIntro from '@app/dashboard/components/outer_layout/FormIntro';
import FormIntroHeading from '@app/dashboard/components/outer_layout/FormIntroHeading';
import PhoneNumberVerification from "./steps/PhoneNumberVerification";
import UserCheck from "./steps/UserCheck";
import ResetPassword from "./steps/ResetPassword";
import OtpStep from "./steps/OtpStep";


const STEPS = {
  USER_NAME_OR_PHONE_NUMBER: 1,
  PHONE_NUMBER_VERIFICATION: 2,
  OTP: 3,
  RESET_PASSWORD: 4,
};

const country: TCountry = getCountryFromDomain();

const ForgetPassword = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { lang } = useLangStore();

  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(STEPS.USER_NAME_OR_PHONE_NUMBER);
  const [emailOrPhoneNumber, setEmailOrPhoneNumber] = useState('');

  const validateField = (fields: IField, setFields: Dispatch<(prevState: IField) => IField>, fieldName: string, value: string) => {

    let valueToValidate = value;

    if (fieldName === 'confirm_password') {
      const passwordValue = fields["password"].value as string;
      const confirmPasswordValue = fields["confirm_password"].value as string;

      const passwordsLiteral = passwordsComparisonLiteral(passwordValue, confirmPasswordValue);
      valueToValidate = passwordsLiteral;
    };

    fieldValidator(fields, setFields, fieldName, valueToValidate);
  };

  const redirectToRegister = () => {
    navigate(OUTER_ROUTES.REGISTER);
  };

  const formSubTitles = {
    [STEPS.USER_NAME_OR_PHONE_NUMBER]: t('outerLayout.form.forgetPassword.desc'),
    [STEPS.PHONE_NUMBER_VERIFICATION]: t('outerLayout.form.forgetPassword.desc'),
    [STEPS.OTP]: t('form.enterTheCode').replace('{{phoneNum}}', emailOrPhoneNumber),
    [STEPS.RESET_PASSWORD]: ""
  };


  return (
    <FormIntro
      heading={
        <FormIntroHeading
          lead={t('outerLayout.welcomeBackHeadingPrefix')}
          emphasis={t('outerLayout.welcomeHeadingBrand')}
        />
      }
      formLegend={
        currentStep === STEPS.RESET_PASSWORD
          ? t('outerLayout.form.forgetPassword.createNewPassword')
          :
          t('outerLayout.form.forgetPassword.forgotPassword')
      }
      subtitle={formSubTitles[currentStep]}
      backBtnProps={
        currentStep === STEPS.OTP ?
          {
            size: 'md',
            variant: 'outlined',
            leadingIcon: lang === 'en' ? ChevronLeft : ChevronRight,
            onClickHandler: () => setCurrentStep(STEPS.PHONE_NUMBER_VERIFICATION)
          }
          :
          undefined
      }
    >
      {
        currentStep === STEPS.USER_NAME_OR_PHONE_NUMBER &&
        <UserCheck
          setEmailOrPhoneNumber={setEmailOrPhoneNumber}
          validateField={validateField}
          loading={loading}
          setLoading={setLoading}
          setCurrentStep={setCurrentStep}
          STEPS={STEPS}
          redirectToRegister={redirectToRegister}
          country={country}
        />
      }
      {
        currentStep === STEPS.PHONE_NUMBER_VERIFICATION &&
        <PhoneNumberVerification
          setEmailOrPhoneNumber={setEmailOrPhoneNumber}
          validateField={validateField}
          loading={loading}
          setLoading={setLoading}
          setCurrentStep={setCurrentStep}
          STEPS={STEPS}
          redirectToRegister={redirectToRegister}
          country={country}
        />
      }
      {
        currentStep === STEPS.OTP &&
        <OtpStep
          loading={loading}
          setLoading={setLoading}
          setCurrentStep={setCurrentStep}
          STEPS={STEPS}
          redirectToRegister={redirectToRegister}
        />
      }
      {
        currentStep === STEPS.RESET_PASSWORD &&
        <ResetPassword
          validateField={validateField}
          loading={loading}
          setLoading={setLoading}
        />
      }
    </FormIntro>
  )
}

export default ForgetPassword;